import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { DialogService } from 'src/app/core/services/dialog.service';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { InputsModule } from '../../../../common/inputs/inputs.module';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import {
  copyImageToBuffer,
  getCertificatePreviewLink,
  handlePluralTranslation,
} from 'src/app/core/utils';
import { Certificate } from 'src/app/core/models/certificate';
import { PageModule } from '../../../../common/page/page.module';
import { LingRegExps } from 'src/app/core/data/ling-reg-exps';
import { QRCodeModule } from 'angularx-qrcode';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SafeUrl } from '@angular/platform-browser';
import { SnackBarService } from 'src/app/core/services/snack-bar.service';
import { BehaviorSubject, forkJoin, from, Observable } from 'rxjs';
import { CertificatesService } from 'src/app/core/services/api/certificates.service';
import { ReleaseCertificateRequest } from 'src/app/core/models/release-certificate-request';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CertificateStatus } from 'src/app/core/enums';
import { FeathersService } from 'src/app/core/services/api/feathers.service';
import { LingSettings } from 'src/app/core/models/ling-settings';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxPrintModule } from 'ngx-print';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CreditsComponent } from 'src/app/content/common/components/credits/credits.component';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { OnboardingService } from 'src/app/core/services/api/onboarding.service';
import {
  releaseCertificateOnboardingTour,
  releaseMultipleCertificatesOnboardingTour,
} from 'src/app/content/private/certificates/components/release-dialog/release-dialog.tour';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    InputsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatButtonModule,
    RouterModule,
    PageModule,
    MatDialogModule,
    DatePipe,
    QRCodeModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslateModule,
    NgxPrintModule,
    CreditsComponent,
    MatIconModule,
    MatDividerModule,
  ],
  providers: [DatePipe],
  templateUrl: './release-dialog.component.html',
  styleUrls: ['./release-dialog.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('false', style({ height: '0px', minHeight: '0' })),
      state('true', style({ height: '*' })),
      transition(
        'false <=> true',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class ReleaseDialogComponent implements OnInit, AfterViewInit {
  protected readonly getCertificatePreviewLink = getCertificatePreviewLink;
  handlePluralTranslation = handlePluralTranslation;

  protected readonly dialogParams: {
    certArray?: Certificate[];
    singleCertificate?: Certificate;
  } = inject(MAT_DIALOG_DATA);
  private readonly message = inject(SnackBarService);
  private readonly dialog = inject(DialogService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly feathers = inject(FeathersService);
  private readonly certificatesService = inject(CertificatesService);
  private readonly translateService = inject(TranslateService);
  private readonly onboardingService = inject(OnboardingService);

  maxDays = 999;
  maxHours = 23;
  maxMinutes = 59;

  releaseForm = this.fb.group({
    sendTo: ['', [Validators.pattern(LingRegExps.email)]],
    days: [null, [Validators.max(this.maxDays)]],
    hours: [null, [Validators.max(this.maxHours)]],
    minutes: [null, [Validators.max(this.maxMinutes)]],
  });
  qrUrl?: SafeUrl;
  loading = false;
  settings?: LingSettings;
  detailsExpanded$ = new BehaviorSubject(false);

  get claimDate(): Date | null {
    const value = this.releaseForm.value;
    let expirationDate: Date | null = null;
    if (value.days || value.hours || value.minutes) {
      expirationDate = new Date();

      if (value.days) {
        expirationDate.setDate(expirationDate.getDate() + value.days);
      }
      if (value.hours) {
        expirationDate.setHours(expirationDate.getHours() + value.hours);
      }
      if (value.minutes) {
        expirationDate.setMinutes(expirationDate.getMinutes() + value.minutes);
      }
    }

    return expirationDate;
  }

  get buttonText(): string {
    if (this.dialogParams.singleCertificate) {
      if (
        this.dialogParams.singleCertificate.status === CertificateStatus.Minted
      ) {
        return this.translateService.instant(
          'dialogs.release.submit.singleCertificate.release'
        );
      }
      if (
        this.dialogParams.singleCertificate.status ===
        CertificateStatus.Released
      ) {
        return this.releaseForm.value.sendTo &&
          this.releaseForm.controls.sendTo.valid
          ? this.translateService.instant(
              'dialogs.release.submit.singleCertificate.send'
            )
          : this.translateService.instant(
              'dialogs.release.submit.singleCertificate.updateRelease'
            );
      }
    } else if (this.dialogParams.certArray?.length) {
      const firstCertificate = this.dialogParams.certArray[0];

      if (firstCertificate.status === CertificateStatus.Minted) {
        return this.translateService.instant(
          'dialogs.release.submit.multipleCertificates.release',
          {
            amount: this.dialogParams.certArray?.length,
            certificateForm: this.translateService.instant(
              handlePluralTranslation(
                this.dialogParams.certArray?.length,
                'certificate'
              )
            ),
          }
        );
      }
      if (firstCertificate.status === CertificateStatus.Released) {
        return this.releaseForm.value.sendTo &&
          this.releaseForm.controls.sendTo.valid
          ? this.translateService.instant(
              'dialogs.release.submit.multipleCertificates.send',
              {
                amount: this.dialogParams.certArray?.length,
                certificateForm: this.translateService.instant(
                  handlePluralTranslation(
                    this.dialogParams.certArray?.length,
                    'certificate'
                  )
                ),
              }
            )
          : this.translateService.instant(
              'dialogs.release.submit.multipleCertificates.updateRelease',
              {
                amount: this.dialogParams.certArray?.length,
                certificateForm: this.translateService.instant(
                  handlePluralTranslation(
                    this.dialogParams.certArray?.length,
                    'certificate'
                  )
                ),
              }
            );
      }
    }

    return this.translateService.instant(
      'dialogs.release.submit.singleCertificate.release'
    );
  }

  get isPaymentNeeded(): boolean {
    return (
      (this.dialogParams.singleCertificate?.status ||
        this.dialogParams?.certArray?.[0]?.status) === CertificateStatus.Minted
    );
  }

  ngOnInit() {
    this.feathers.settingsObject$.subscribe((settings) => {
      this.settings = settings;
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.dialogParams.singleCertificate) {
        this.onboardingService.registerCurrentAvailableTour(
          releaseCertificateOnboardingTour
        );
      } else {
        this.onboardingService.registerCurrentAvailableTour(
          releaseMultipleCertificatesOnboardingTour
        );
      }

      this.onboardingService.startCurrentAvailableTourIfNotMuted();
    });
  }

  copyImage(safeUrl?: SafeUrl): void {
    copyImageToBuffer(safeUrl)?.subscribe({
      next: () => {
        this.message.success(
          this.translateService.instant('snackbar.success.qrCodeCopied')
        );
      },
      error: () => {
        this.message.warn(
          this.translateService.instant('snackbar.warn.imageCopyNotSupported')
        );
      },
    });
  }

  copyText(text: string): void {
    from(navigator.clipboard.writeText(text)).subscribe(() => {
      this.message.success(
        this.translateService.instant('snackbar.success.tokenCopied')
      );
    });
  }

  release(): void {
    this.loading = true;
    const value = this.releaseForm.value;
    let sendTo: string | undefined;
    if (value.sendTo) {
      sendTo = value.sendTo;
    }

    let result: ReleaseCertificateRequest | ReleaseCertificateRequest[];
    if (this.dialogParams?.singleCertificate) {
      result = {
        certificate: this.dialogParams?.singleCertificate,
        expirationDate: this.claimDate,
        sendTo,
      };
    }
    if (this.dialogParams?.certArray) {
      result = this.dialogParams?.certArray.map((certificate) => ({
        certificate,
        expirationDate: this.claimDate,
        sendTo,
      }));
    }

    let call: Observable<any>;

    if (Array.isArray(result!)) {
      call = forkJoin(
        result.map((req) => this.certificatesService.releaseCertificate(req))
      );
    } else {
      call = this.certificatesService.releaseCertificate(result!);
    }

    call.subscribe({
      next: (res) => {
        this.loading = false;
        let message;
        if (Array.isArray(res)) {
          const firstCertificate = this.dialogParams.certArray?.[0];
          if (firstCertificate?.status === CertificateStatus.Minted) {
            message = this.translateService.instant(
              'snackbar.success.multipleCertificatesReleased',
              { amount: res.length }
            );
          } else {
            message = this.translateService.instant(
              'snackbar.success.multipleCertificatesReleaseUpdated',
              { amount: res.length }
            );
          }
        } else {
          const cert = res ?? this.dialogParams.singleCertificate;
          const name =
            cert?.name ??
            cert?.general?.productTitle ??
            this.dialogParams.singleCertificate?.general?.productTitle ??
            '';
          if (
            this.dialogParams.singleCertificate?.status ===
            CertificateStatus.Minted
          ) {
            message = this.translateService.instant(
              'snackbar.success.singleCertificateReleased',
              { certificateName: name }
            );
          } else {
            message = this.translateService.instant(
              'snackbar.success.singleCertificateReleaseUpdated',
              { certificateName: name }
            );
          }
        }
        this.message.success(message);
        this.dialog.closeCurrent(res);
      },
      error: () => {
        this.loading = false;
      },
    });
  }
}
